<script lang="ts">
  import { browser } from '$app/environment';
  import { page } from '$app/stores';
  import HackathonEventInfoBox from '$lib/components/reusable/hackathons/HackathonEventInfoBox.svelte';
  import { Sidenav } from '$components/reusable/navigation/Sidenav';
  import { client } from '$lib/api/Client';
  import {
    isRouteAccessibleWithoutAuth,
    onboardingAllowedRoutes,
    shouldShowHackathonBanner,
  } from '$lib/routing-utils';
  import { sideNavOpen } from '$stores';
  import { onMount, setContext } from 'svelte';
  import { derived, writable } from 'svelte/store';
  import { slide } from 'svelte/transition';
  import BugReport from '$components/reusable/moderation/BugReport.svelte';
  import { AccountRoles } from '$shared/lib';
  import CreateEditAnnouncement from '$components/reusable/admin/CreateEditAnnouncement.svelte';
  import { beforeNavigate } from '$app/navigation';
  import { clearExpiredProjectsLocalStorage } from '$utils';
  import type { LayoutData } from './$types';
  import * as Sentry from '@sentry/sveltekit';
  import PostEditor from '$lib/components/reusable/posts/editor/PostEditor.svelte';
  import WelcomeModal from '$lib/components/reusable/WelcomeModal.svelte';
  import UserProfilePreview from '$lib/components/global/UserProfilePreview.svelte';
  import type { ListUnreadConversationsResponse } from '$shared/api';
  import UpdateNotification from '$lib/components/UpdateNotification.svelte';

  export const unreadConversations = writable<UnreadConversationRecord>({});
  export let data: LayoutData;

  let showBugReportModal = false;
  let showAnnouncementCreateModal = false;

  type UnreadConversationRecord = Record<number, { lastReadAt: number; lastMessageAt: number }>;
  const unreadConversationCount = derived(
    unreadConversations,
    ($unreadConversations) => Object.keys($unreadConversations).length,
  );
  setContext('unreadConversations', unreadConversations);
  setContext('unreadConversationCount', unreadConversationCount);

  async function getUnreadConversations() {
    try {
      // Don't unnecessarily poll before a user is even onboarded
      if (!browser || !data.user || !data.user.agreementComplete) return;
      const unreadConversationResponse: ListUnreadConversationsResponse =
        await client.chats.getUnreadConversations();

      const newUnreadConversations = unreadConversationResponse
        // This filter is kinda janky, but basically gives a small window before we'll render a convo as unread,
        // to allow for the roundtrip of marking it as read if the user is actively reading it. This only
        // matters in a small edge-case where the message hits the server right before a poll comes in, and the
        // poll query immediately picks it up as unread before the client has a chance to render / act on it, but the poll
        // response arrives AFTER the websocket has delivered the message.
        .filter(
          ({ id, lastReadAt, lastMessageAt }) =>
            lastMessageAt <= Date.now() - 1500 ||
            ($unreadConversations[id] && lastReadAt < lastMessageAt),
        )
        .reduce<UnreadConversationRecord>((acc, { id, lastReadAt, lastMessageAt }) => {
          acc[id] = { lastReadAt, lastMessageAt };
          return acc;
        }, {});

      // Only update the store if the unread conversations have changed
      // otherwise we get a lot of unnecessary re-renders
      const newUnreads = Object.keys(newUnreadConversations);
      const oldUnreads = Object.keys($unreadConversations);

      const keysEqual =
        newUnreads.length === oldUnreads.length &&
        newUnreads.every((value, index) => value === oldUnreads[index]);
      if (!keysEqual) {
        unreadConversations.set(newUnreadConversations);
      }
    } catch (error) {
      console.error('Failed to fetch unread conversations:', error);
    }
  }

  const markConversationRead = async (conversationId: number, messageReadAt: number) => {
    unreadConversations.update((currentConversations) => {
      if (
        currentConversations[conversationId] &&
        currentConversations[conversationId].lastMessageAt <= messageReadAt
      ) {
        delete currentConversations[conversationId];
      }
      return { ...currentConversations };
    });
  };
  setContext('markConversationRead', markConversationRead);

  onMount(() => {
    Sentry.setUser({
      id: data.userId,
      email: data.email,
      username: data.username ?? undefined,
    });
  });

  onMount(() => {
    getUnreadConversations();
    const interval = setInterval(async () => {
      await getUnreadConversations();
    }, 15000); // Poll for unreads every 15 seconds

    return () => {
      clearInterval(interval);
    };
  });

  beforeNavigate(({ to }) => {
    if (to?.url.pathname.startsWith('/projects')) {
      clearExpiredProjectsLocalStorage();
    }
  });
</script>

<div class="relative mx-auto flex h-full w-full max-w-[1600px]">
  <!-- {#if !widgetBotDisabledRoutes.has($page.url.pathname) && !onboardingAllowedRoutes.has($page.url.pathname) && $page.data.agreementComplete}
    <WidgetBot />
  {/if} -->
  <aside
    class="bg-gray-dark-0 sticky top-[4rem] hidden h-[calc(100vh_-_4rem)] max-h-screen w-1/4 shrink-0 basis-1/4 lg:block"
  >
    {#if !$page.data.onboardingComplete && (onboardingAllowedRoutes.has($page.url.pathname) || isRouteAccessibleWithoutAuth($page.url.pathname))}
      <div class="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[3px]"></div>
    {/if}
    <Sidenav bind:showBugReportModal bind:showAnnouncementCreateModal bind:data />
  </aside>
  {#if $sideNavOpen}
    <div
      transition:slide={{ axis: 'x' }}
      class="bg-gray-dark-0 fixed top-[4rem] z-50 h-[calc(100vh_-_4rem)] max-h-screen w-full lg:hidden"
    >
      {#if !$page.data.onboardingComplete && (onboardingAllowedRoutes.has($page.url.pathname) || isRouteAccessibleWithoutAuth($page.url.pathname))}
        <div class="absolute inset-0 z-10 backdrop-blur-[3px]"></div>
      {/if}
      <Sidenav bind:showBugReportModal bind:showAnnouncementCreateModal bind:data />
    </div>
  {/if}

  <!-- Modals -->
  <BugReport bind:showBugReportModal />
  {#if $page.data.accountRoles?.includes(AccountRoles.ADMIN)}
    <CreateEditAnnouncement bind:showAnnouncementCreateModal />
  {/if}

  <main class="mt-xl px-2xl xl:mt-2xl z-10 w-1/2 flex-1 xl:p-0">
    {#if data.hackathon && shouldShowHackathonBanner($page.url.pathname)}
      <HackathonEventInfoBox hackathon={data.hackathon} />
    {/if}
    <slot />
  </main>
  {#if !$page.data.hideRightAside}
    <aside class="sticky top-[4rem] hidden h-fit w-1/4 shrink-0 basis-1/4 xl:block">
      <!-- This is using a dynamic component -->
      <!-- For more information see here: https://github.com/sveltejs/kit/issues/627#issuecomment-1458539744 -->
      {#if $page.data.rightAside}
        <svelte:component this={$page.data.rightAside} />
      {/if}
    </aside>
  {/if}
</div>

{#if browser}
  <PostEditor forums={data.forums ?? []} />
{/if}

{#if data.user?.onboardingComplete && !data.user?.agreementComplete}
  <WelcomeModal />
{/if}

<UserProfilePreview />

<!-- Shows the user a notification when an update is available -->
<UpdateNotification />
